/**
 * useServiceOrderChecklists - Checklists sequenciais da OS (Fase 8)
 *
 * Lê, via RPC, os templates configurados no tipo de serviço por etapa
 * (Início → Execução → Conclusão) e o checklist gerado para cada um.
 * A geração/liberação das etapas acontece no servidor (BR-OS-005).
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-10 v1.1.0 - Expõe prazo e progresso por itens retornados
 *            pela mesma RPC da aba.
 *   RELEASE: 2026-10-08 v1.0.0 - Criação inicial.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ServiceChecklistStage } from '@/hooks/useServiceTypes';

export interface ServiceOrderChecklistRow {
  stage: ServiceChecklistStage;
  template_id: string;
  template_name: string;
  instance_id: string | null;
  instance_code: number | null;
  instance_status: string | null;
  finished_at: string | null;
  /** Prazo final vigente da vistoria vinculada à O.S. */
  due_at: string | null;
  /** Situação calculada do prazo para sinalização visual. */
  deadline_status: string | null;
  /** Total de itens materializados na instância. */
  items_total: number;
  /** Itens que já possuem uma resposta salva. */
  items_answered: number;
}

export type StageState = 'locked' | 'active' | 'done';

/** Etapa liberada quando todas as anteriores estão aprovadas (regra pura, espelha o servidor). */
export function computeStageStates(rows: ServiceOrderChecklistRow[]): Record<ServiceChecklistStage, StageState | null> {
  const order: ServiceChecklistStage[] = ['start', 'execution', 'completion'];
  const result = { start: null, execution: null, completion: null } as Record<ServiceChecklistStage, StageState | null>;
  let blocked = false;
  for (const stage of order) {
    const items = rows.filter((r) => r.stage === stage);
    if (items.length === 0) continue;
    const done = items.every((r) => r.instance_status === 'approved');
    result[stage] = blocked ? 'locked' : done ? 'done' : 'active';
    if (!done) blocked = true;
  }
  return result;
}

// Preserva o `this` do cliente (supabase.rpc usa this.rest)
const rpc = (fn: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (f: string, a: Record<string, unknown>) => Promise<{ data: any; error: any }>).call(supabase, fn, args);

export function useServiceOrderChecklists(serviceOrderId: string | null) {
  return useQuery({
    queryKey: ['service-order-checklists', serviceOrderId],
    enabled: !!serviceOrderId,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await rpc('get_service_order_checklists', { p_id: serviceOrderId, p_tenant_hint: null });
      if (error) throw error;
      return (data ?? []) as ServiceOrderChecklistRow[];
    },
  });
}
